(() => {
  'use strict';
  if (window.ACOProjectShopping) return;

  const KEY = 'aco.activeProject.v2';
  const LEGACY_KEY = 'aco.activeProject.v1';
  const PREFIX = '#aco_project=';
  const LEGACY_MAX_AGE = 12 * 60 * 60 * 1000;
  let active = null;

  function validate(value) {
    if (!value || value.version !== 1) return null;

    if (
      typeof value.projectId !== 'string' ||
      !/^[A-Za-z0-9_-]{1,100}$/.test(value.projectId)
    ) return null;

    if (
      typeof value.projectName !== 'string' ||
      !value.projectName.trim() ||
      value.projectName.length > 160
    ) return null;

    const project = {
      version: 1,
      projectId: value.projectId,
      projectName: value.projectName.trim(),
    };

    for (const key of [
      'year',
      'make',
      'model',
      'paintCode',
      'colorName',
      'temperatureF',
      'projectType',
      'paintSystem',
    ]) {
      if (
        typeof value[key] === 'string' &&
        value[key].length <= 160
      ) {
        project[key] = value[key];
      }
    }

    return project;
  }

  function persist() {
    const saved = JSON.stringify({ version: 2, project: active?.project || null });
    try {
      localStorage.setItem(KEY, saved);
    } catch {}
    try {
      sessionStorage.setItem(KEY, saved);
      sessionStorage.removeItem(LEGACY_KEY);
    } catch {}
  }

  function getActiveProject() {
    return active ? { ...active.project } : null;
  }

  function render() {
    const project = getActiveProject();

    document.querySelectorAll('aco-project-fields').forEach(fields => fields.sync?.());

    document.querySelectorAll('[data-aco-project-bar]').forEach(bar => {
      bar.hidden = !project;

      const name = bar.querySelector('[data-aco-project-name]');
      if (name) {
        name.textContent = project ? project.projectName : '';
      }
    });
  }

  function announce() {
    render();

    window.dispatchEvent(new CustomEvent('aco:project-change', {
      detail: { project: getActiveProject() },
    }));
  }

  function readSelection() {
    if (!location.hash.startsWith(PREFIX)) return;

    const fragment = location.hash.slice(PREFIX.length);

    try {
      const project = fragment.length <= 12000
        ? validate(JSON.parse(decodeURIComponent(fragment)))
        : null;

      active = project
        ? { project }
        : null;
    } catch {
      active = null;
    }

    persist();

    try {
      history.replaceState(
        history.state,
        '',
        location.pathname + location.search
      );
    } catch {}

    announce();
  }

  function stop() {
    active = null;
    persist();
    announce();
  }

  function restore() {
    let raw;
    try {
      raw = localStorage.getItem(KEY);
    } catch {
      try {
        raw = sessionStorage.getItem(KEY);
      } catch {
        return;
      }
    }

    if (raw !== null) {
      try {
        const saved = JSON.parse(raw);
        const project = saved?.version === 2 ? validate(saved.project) : null;
        active = project ? { project } : null;
      } catch {
        active = null;
      }
      try {
        sessionStorage.removeItem(LEGACY_KEY);
      } catch {}
      return;
    }

    active = null;
    try {
      const saved = JSON.parse(sessionStorage.getItem(LEGACY_KEY));
      const project = validate(saved?.project);
      if (
        project &&
        Number.isFinite(saved.expiresAt) &&
        saved.expiresAt > Date.now() &&
        saved.expiresAt <= Date.now() + LEGACY_MAX_AGE
      ) {
        active = { project };
      }
    } catch {}
    persist();
  }

  restore();

  if (!customElements.get('aco-project-fields')) {
    customElements.define('aco-project-fields', class extends HTMLElement {
      connectedCallback() {
        this.onSync = this.onSync || this.sync.bind(this);
        this.form = this.closest('form');
        window.addEventListener('aco:project-change', this.onSync);
        for (const name of ['pointerdown', 'click', 'keydown', 'submit', 'formdata']) {
          this.form?.addEventListener(name, this.onSync, true);
        }
        this.sync();
      }

      disconnectedCallback() {
        window.removeEventListener('aco:project-change', this.onSync);
        for (const name of ['pointerdown', 'click', 'keydown', 'submit', 'formdata']) {
          this.form?.removeEventListener(name, this.onSync, true);
        }
      }

      sync(event) {
        const project = getActiveProject();
        const values = {
          _aco_project_id: project?.projectId || '',
          _aco_project_name: project?.projectName || '',
        };
        const changed = [];
        for (const [key, value] of Object.entries(values)) {
          const input = this.querySelector('input[name="properties[' + key + ']"]');
          if (input && (input.value !== value || input.disabled !== !project)) {
            input.value = value;
            input.disabled = !project;
            changed.push(input);
          }
          if (event?.formData) {
            event.formData.delete('properties[' + key + ']');
            if (project) event.formData.set('properties[' + key + ']', value);
          }
        }
        for (const input of changed) {
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  window.ACOProjectShopping = Object.freeze({
    getActiveProject,
    stop,
  });

  document.addEventListener('click', event => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-aco-project-stop]')
      : null;

    if (!button) return;

    event.preventDefault();
    stop();
  });

  window.addEventListener('hashchange', readSelection);

  window.addEventListener('pageshow', () => {
    restore();
    readSelection();
    announce();
  });

  window.addEventListener('storage', event => {
    try {
      if (event.storageArea !== localStorage) return;
    } catch {
      return;
    }
    if (event.key !== KEY && event.key !== null) return;
    try {
      sessionStorage.removeItem(LEGACY_KEY);
      sessionStorage.removeItem(KEY);
    } catch {}
    restore();
    announce();
  });

  document.addEventListener('shopify:section:load', render);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      readSelection();
      render();
    });
  } else {
    readSelection();
    render();
  }
})();